import React, { useEffect, useState } from 'react';
import { Box, Container, Typography, Paper, Stack, CssBaseline, CircularProgress } from '@mui/material';
import { Outlet, useParams, Link as RouterLink } from 'react-router-dom';
import { Link as MuiLink } from '@mui/material';
import api from '../../api';
import { PORTAL_CONSENT_FOOTER, portalTokenErrorMessage } from './portalTokenMessages';

/**
 * ZRF-WTPB — public portal token shell (mobile-first).
 * Validates token via API; URL contains only the token (no PII).
 */
const PortalTokenShell = () => {
  const { token } = useParams();
  const tokenHint =
    typeof token === 'string' && token.length > 0 ? `${token.slice(0, 4)}…` : '—';

  const [validationLoading, setValidationLoading] = useState(true);
  const [validation, setValidation] = useState(null);
  const [validationError, setValidationError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!token) {
        setValidation({ valid: false, reason: 'invalid' });
        setValidationError(portalTokenErrorMessage({ valid: false }));
        setValidationLoading(false);
        return;
      }
      setValidationLoading(true);
      setValidationError('');
      try {
        const res = await api.get(
          `/api/portal/tokens/${encodeURIComponent(token)}/validate`
        );
        if (cancelled) return;
        setValidation(res.data || { valid: false, reason: 'invalid' });
        if (res.data?.valid === false) {
          setValidationError(portalTokenErrorMessage(res.data));
        }
        console.log('🔍 portal token validate', {
          valid: res.data?.valid,
          actionType: res.data?.actionType,
        });
      } catch (err) {
        console.error('❌ portal token validate failed:', err);
        if (!cancelled) {
          const data = err?.response?.data;
          setValidation(data && typeof data === 'object' ? data : { valid: false, reason: 'invalid' });
          setValidationError(portalTokenErrorMessage(data));
        }
      } finally {
        if (!cancelled) setValidationLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [token]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'background.default',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <CssBaseline />
      <Box
        component="header"
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Container maxWidth="sm" disableGutters>
          <Typography variant="h6" component="p" sx={{ fontWeight: 600, m: 0 }}>
            ZenRentFlow
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Secure tenant link
          </Typography>
        </Container>
      </Box>

      <Container
        maxWidth="sm"
        sx={{
          flexGrow: 1,
          py: { xs: 2.5, sm: 4 },
          px: { xs: 2, sm: 3 },
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2.5, sm: 3 },
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          {validationLoading ? (
            <Stack alignItems="center" spacing={2} sx={{ py: 4 }}>
              <CircularProgress size={36} />
              <Typography variant="body2" color="text.secondary">
                Checking your secure link…
              </Typography>
            </Stack>
          ) : (
            <Outlet
              context={{
                token,
                tokenHint,
                validation,
                validationLoading,
                validationError,
              }}
            />
          )}
        </Paper>

        <Stack spacing={1} sx={{ mt: 3, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            {PORTAL_CONSENT_FOOTER}
          </Typography>
          {validation?.valid && validation.actionType === 'submit_maintenance' ? (
            <MuiLink
              component={RouterLink}
              to={`/p/t/${encodeURIComponent(token || '')}/maintenance/new`}
              variant="caption"
              underline="hover"
            >
              Submit a maintenance request
            </MuiLink>
          ) : null}
          {validation?.valid && validation.actionType === 'confirm_access' ? (
            <MuiLink
              component={RouterLink}
              to={
                validation.relatedEntityId
                  ? `/p/t/${encodeURIComponent(token || '')}/access/${encodeURIComponent(validation.relatedEntityId)}`
                  : `/p/t/${encodeURIComponent(token || '')}/access`
              }
              variant="caption"
              underline="hover"
            >
              Respond to access request
            </MuiLink>
          ) : null}
          {validation?.valid && validation.actionType === 'view_documents' ? (
            <MuiLink
              component={RouterLink}
              to={`/p/t/${encodeURIComponent(token || '')}/documents`}
              variant="caption"
              underline="hover"
            >
              View documents
            </MuiLink>
          ) : null}
        </Stack>
      </Container>
    </Box>
  );
};

export default PortalTokenShell;
